import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, Users, ChevronRight, ChevronLeft, Calendar, Clock, Play, Target, Swords } from '../components/common/Icons';
import { useLeaderboards } from '../context/LeaderboardContext';
import { useTournaments } from '../context/TournamentContext';
import { getPlayersByIds, getPlayersPaginated, getPlatformStats, getTeamOwnerIds } from '../services/database';
import PlayerSearchBar from '../components/common/PlayerSearchBar';
import TournamentLogo from '../components/common/TournamentLogo';
import PlayerCard from '../components/features/stats/PlayerCard';

const TEAL = '#31C590';
const MINT = '#23F8B5';
const DISCORD_URL = 'https://discord.gg/rematchpro';

/* Accent-word display title (goalsverse-style: bold condensed, one word highlighted) */
const DisplayTitle = ({ pre, accent, className = '' }) => (
  <h2 className={`font-bebas tracking-wide leading-none ${className}`}>
    <span className="text-white">{pre} </span>
    <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, ${TEAL}, ${MINT})` }}>
      {accent}
    </span>
  </h2>
);


const StatCell = ({ value, label }) => (
  <div className="flex-1 text-center px-2">
    <div className="font-bebas text-3xl sm:text-4xl leading-none" style={{ color: TEAL }}>{value}</div>
    <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest mt-1">{label}</div>
  </div>
);

/* Club crest for the podium — real team logo with an initials fallback */
const ClubCrest = ({ logo, name, accent, big }) => {
  const [err, setErr] = useState(false);
  const cls = big ? 'w-32 h-32 sm:w-40 sm:h-40 mb-4' : 'w-24 h-24 sm:w-28 sm:h-28 mb-3';
  if (logo && !err) {
    return (
      <div
        className={`overflow-hidden rounded-full transition-transform duration-300 group-hover:scale-105 ${cls}`}
        style={{ border: `2px solid ${accent}66`, boxShadow: `0 6px 22px ${accent}55` }}
      >
        <img
          src={logo}
          alt={name}
          onError={() => setErr(true)}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  return (
    <div
      className={`flex items-center justify-center rounded-full font-bebas text-white transition-transform duration-300 group-hover:scale-105 ${cls}`}
      style={{ background: `radial-gradient(circle at 50% 35%, ${accent}44, rgba(255,255,255,0.04))`, boxShadow: `0 6px 22px ${accent}44, inset 0 0 0 2px ${accent}55` }}
    >
      <span className={big ? 'text-5xl' : 'text-4xl'}>{(name || '?').charAt(0).toUpperCase()}</span>
    </div>
  );
};

const useResponsivePerView = () => {
  const [perView, setPerView] = useState(3);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w < 640 ? 1 : w < 1024 ? 2 : 3);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);
  return perView;
};

/* Auto-advancing slideshow of tournament cards with arrows + dots */
const TournamentCarousel = ({ items, renderCard }) => {
  const perView = useResponsivePerView();
  const maxIndex = Math.max(0, items.length - perView);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const cardBasis = 100 / perView;

  useEffect(() => { setIndex((i) => Math.min(i, maxIndex)); }, [maxIndex]);

  useEffect(() => {
    if (paused || items.length <= perView) return;
    const id = setInterval(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), 4500);
    return () => clearInterval(id);
  }, [paused, items.length, perView, maxIndex]);

  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  const next = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  const hasControls = items.length > perView;

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Track region — arrows sit in the side gutters, off the cards */}
      <div className="relative sm:px-14">
        <div className="overflow-hidden">
          <div
            className={`flex transition-transform duration-500 ease-out ${hasControls ? '' : 'justify-center'}`}
            style={{ transform: `translateX(-${index * cardBasis}%)` }}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className="px-2 flex-shrink-0"
                style={{ flexBasis: `${cardBasis}%`, maxWidth: `${cardBasis}%` }}
              >
                {renderCard(item)}
              </div>
            ))}
          </div>
        </div>

        {hasControls && (
          <>
            <button
              onClick={prev}
              aria-label="Previous"
              className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-zinc-900/90 border border-white/10 text-white hover:bg-zinc-800 shadow-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next"
              className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-zinc-900/90 border border-white/10 text-white hover:bg-zinc-800 shadow-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {hasControls && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="h-2 rounded-full transition-all duration-300"
              style={{
                width: i === index ? 24 : 8,
                backgroundColor: i === index ? TEAL : 'rgba(255,255,255,0.2)'
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const renderTournamentCard = (tour, t) => {
  const isLive = ['group_stage', 'knockout', 'in_progress', 'running', 'playoffs'].includes(tour.status);
  return (
    <Link
      to={`/tournaments/${tour.id}`}
      className="group block rounded-2xl border border-white/[0.08] bg-zinc-900/60 backdrop-blur-md p-5 hover:border-[#31C590]/40 hover:bg-zinc-900/75 transition-all duration-300"
    >
      <div className="flex items-start gap-3 mb-4">
        <TournamentLogo tournament={tour} size="w-12 h-12" />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white truncate group-hover:text-[#31C590] transition-colors">{tour.name}</h3>
          <span className="text-xs text-gray-500 capitalize">{(tour.format || '').replace(/_/g, ' ')}</span>
        </div>
        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase ${isLive ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
          {isLive ? <Play className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {isLive ? t('home.live', { defaultValue: 'Live' }) : t('home.upcoming', { defaultValue: 'Soon' })}
        </span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-gray-400">
          <Users className="w-4 h-4" />
          {(tour.registeredTeams?.length ?? 0)}/{tour.maxTeams ?? '—'}
        </span>
        {tour.prize && (
          <span className="flex items-center gap-1.5 font-semibold" style={{ color: TEAL }}>
            <Trophy className="w-4 h-4" /> {tour.prize}
          </span>
        )}
      </div>
    </Link>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const {
    topGoalScorers, topAssistLeaders, topPassLeaders, topInterceptionLeaders,
    topSaveLeaders, mvpLeaders, topTeams, loadLeaderboards
  } = useLeaderboards();
  const { getActiveTournaments, getUpcomingTournaments } = useTournaments();
  const [mounted, setMounted] = useState(false);
  const [titleLeaders, setTitleLeaders] = useState([]);
  const [fullById, setFullById] = useState({});      // player_id -> full computed row
  const [platformStats, setPlatformStats] = useState({ players: 0, teams: 0, tournaments: 0, live: 0 });
  const [ownerIds, setOwnerIds] = useState({});      // team_id -> owner user id

  useEffect(() => { loadLeaderboards(10); }, [loadLeaderboards]);
  useEffect(() => {
    let active = true;
    // Most titles — sort by computed titles (users.tournaments_won is stale).
    getPlayersPaginated({ sortBy: 'titles', sortOrder: 'desc', limit: 5, offset: 0 })
      .then((r) => { if (active && r?.success) setTitleLeaders(r.data || []); }).catch(() => {});
    // Real platform totals for the stats bar.
    getPlatformStats().then((r) => { if (active && r?.success) setPlatformStats(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(timer);
  }, []);

  const live = getActiveTournaments?.() || [];
  const upcoming = getUpcomingTournaments?.() || [];
  const showcase = [...live, ...upcoming].slice(0, 6);
  const fmt = (n) => (n ?? 0).toLocaleString();

  // Best player in each category — each from its OWN leaderboard (not derived from
  // the goal-scorers pool). Ordered so the "Top Scorer" sits in the middle of the fan.
  const first = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);
  const mvpLeader = first(mvpLeaders);
  const titleLeader = first(titleLeaders);

  // Fetch full computed rows (real titles + mvps + stats) for ONLY the ~7 leader
  // cards, targeted server-side so we transfer a few hundred bytes.
  const leaderIds = [
    first(topSaveLeaders), first(topAssistLeaders), first(topInterceptionLeaders),
    first(topGoalScorers), first(topPassLeaders), mvpLeader, titleLeader
  ].map((p) => p?.player_id).filter(Boolean);
  const leaderIdsKey = leaderIds.slice().sort().join(',');
  useEffect(() => {
    if (!leaderIdsKey) return;
    let active = true;
    getPlayersByIds(leaderIdsKey.split(',')).then((r) => {
      if (active && r?.success && Array.isArray(r.data)) {
        const m = {};
        r.data.forEach((row) => { if (row.player_id != null) m[row.player_id] = row; });
        setFullById(m);
      }
    }).catch(() => {});
    return () => { active = false; };
  }, [leaderIdsKey]);

  // Merge in the full computed row, normalising the fields the card reads.
  const enrichPlayer = (p) => {
    if (!p) return null;
    const full = fullById[p.player_id] || {};
    const merged = { ...full, ...p };
    merged.mvp_count = full.total_mvps ?? p.mvp_count ?? p.total_mvps ?? merged.mvp_count ?? 0;
    merged.tournaments_won = full.tournaments_won ?? p.tournaments_won ?? merged.tournaments_won ?? 0;
    if (merged.win_rate == null && full.win_percentage != null) merged.win_rate = full.win_percentage;
    return merged;
  };

  const leaders = [
    { player: enrichPlayer(first(topSaveLeaders)), key: 'total_saves', badge: t('home.cat.keeper', { defaultValue: 'TOP KEEPER' }), label: 'SAVES', accent: '#a855f7', image: '/players/1.webp' },
    { player: enrichPlayer(first(topInterceptionLeaders)), key: 'total_interceptions', badge: t('home.cat.defender', { defaultValue: 'TOP DEFENDER' }), label: 'INTERCEPTIONS', accent: '#f97316', image: '/players/3.webp' },
    { player: enrichPlayer(first(topAssistLeaders)), key: 'total_assists', badge: t('home.cat.playmaker', { defaultValue: 'PLAYMAKER' }), label: 'ASSISTS', accent: '#3b82f6', image: '/players/2.webp' },
    { player: enrichPlayer(first(topGoalScorers)), key: 'total_goals', badge: t('home.cat.scorer', { defaultValue: 'TOP SCORER' }), label: 'GOALS', accent: '#31C590', image: '/players/4.webp' },
    { player: enrichPlayer(mvpLeader), key: 'mvp_count', badge: t('home.cat.mvp', { defaultValue: 'MOST MVPs' }), label: 'MVPS', accent: '#f59e0b', image: '/players/6.webp' },
    { player: enrichPlayer(first(topPassLeaders)), key: 'total_passes', badge: t('home.cat.passer', { defaultValue: 'TOP PASSER' }), label: 'PASSES', accent: '#06b6d4', image: '/players/5.webp' },
    { player: enrichPlayer(titleLeader), key: 'tournaments_won', badge: t('home.cat.titles', { defaultValue: 'MOST TITLES' }), label: 'TITLES', accent: '#eab308', image: '/players/7.webp' }
  ].filter((l) => l.player);
  const fanCenter = Math.floor(leaders.length / 2);

  // Top 3 clubs by titles won → podium (2nd · 1st · 3rd), gold / silver / bronze
  const topClubs = [...(topTeams || [])]
    .sort((a, b) => (b.tournaments_won ?? 0) - (a.tournaments_won ?? 0))
    .slice(0, 3);
  const PODIUM = {
    1: { accent: '#FBBF24', place: '1ST', big: true },
    2: { accent: '#CBD5E1', place: '2ND', big: false },
    3: { accent: '#D08A50', place: '3RD', big: false }
  };
  const podiumOrder = topClubs.length === 3 ? [topClubs[1], topClubs[0], topClubs[2]] : topClubs;

  // Owner user-ids for the podium clubs, so the owner name links to a profile.
  const clubTeamIdsKey = topClubs.map((c) => c.team_id).filter(Boolean).slice().sort().join(',');
  useEffect(() => {
    if (!clubTeamIdsKey) return;
    let active = true;
    getTeamOwnerIds(clubTeamIdsKey.split(',')).then((r) => { if (active && r?.success) setOwnerIds(r.data || {}); }).catch(() => {});
    return () => { active = false; };
  }, [clubTeamIdsKey]);

  return (
    <div className="relative bg-[#12151e] text-white overflow-hidden">
      {/* ============================= HERO ============================= */}
      <section className="relative">
        {/* Video background (no overflow-clip + lifted above the stats bar so the
            search platform dropdown isn't cut off) */}
        <div className="relative z-20">
          <video
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
            poster="/background1.webp"
          >
            <source src="/background/hero.webm" type="video/webm" />
          </video>
          {/* Teal wash */}
          <div className="absolute inset-0" style={{ background: `radial-gradient(70% 90% at 50% 0%, rgba(49,197,144,0.16), rgba(18, 21, 30,0) 65%)` }} />
          {/* Glassy frosted mask (blurs the video like frosted glass) */}
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/[0.03]" />
          {/* Foggy haze — soft mist, thicker toward the bottom */}
          <div className="absolute inset-0" style={{ background: 'radial-gradient(100% 65% at 50% 32%, rgba(255,255,255,0.06), rgba(255,255,255,0) 55%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(200,232,224,0) 28%, rgba(178,220,208,0.10) 68%, rgba(150,206,190,0.18) 100%)' }} />
          {/* Dark gradient for text readability */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18, 21, 30,0.42), rgba(18, 21, 30,0.70) 70%, rgba(18, 21, 30,0.97))' }} />

          <div className={`relative max-w-3xl mx-auto px-4 pt-20 pb-16 text-center transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h1 className="font-bebas leading-[0.9] tracking-wide mb-5 drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
              <span className="block text-5xl sm:text-7xl text-white">{t('home.title', { defaultValue: 'TRACK ANY' })}</span>
              <span
                className="block text-5xl sm:text-7xl bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(90deg, ${TEAL}, ${MINT})` }}
              >
                {t('home.titleAccent', { defaultValue: 'REMATCH PLAYER' })}
              </span>
            </h1>
            <p className="max-w-xl mx-auto text-gray-300 mb-8 text-base sm:text-lg drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)]">
              {t('home.subtitle', {
                defaultValue: 'Search any player to follow their rating, win rate and full match history — no account needed.'
              })}
            </p>
            <PlayerSearchBar size="hero" />
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative border-y border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 py-6 flex divide-x divide-white/[0.06]">
            <StatCell value={fmt(platformStats.tournaments)} label={t('home.stats.tournaments', { defaultValue: 'Tournaments' })} />
            <StatCell value={fmt(platformStats.teams)} label={t('home.stats.teams', { defaultValue: 'Teams' })} />
            <StatCell value={fmt(platformStats.players)} label={t('home.stats.players', { defaultValue: 'Players' })} />
            <StatCell value={fmt(platformStats.live)} label={t('home.stats.live', { defaultValue: 'Live & Upcoming' })} />
          </div>
        </div>
      </section>

      {/* ===================== BEST PLAYERS (card fan) ===================== */}
      <section
        className="relative pt-16 pb-8 bg-center bg-cover"
        style={{
          // Rematch backdrop masked behind the cards (goalsverse-style): a heavy
          // dark overlay over the image, fading to the page colour at top/bottom.
          backgroundImage:
            "linear-gradient(180deg, #12151e 0%, rgba(18, 21, 30,0.86) 14%, rgba(18, 21, 30,0.86) 86%, #12151e 100%), url('/newbkkg.webp')"
        }}
      >
        <div className="max-w-6xl mx-auto px-4 text-center mb-10 relative">
          <DisplayTitle
            pre={t('home.best', { defaultValue: 'BEST' })}
            accent={t('home.playersWord', { defaultValue: 'PLAYERS' })}
            className="text-3xl sm:text-4xl"
          />
          <p className="text-gray-400 mt-2">{t('home.bestSub', { defaultValue: 'The category leaders across the platform right now.' })}</p>
        </div>

        {leaders.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">{t('home.noPlayers', { defaultValue: 'No players yet.' })}</p>
        ) : (
          // Full-width scroll rail: the card row sits directly on the page, centred
          // when it fits and scrolling horizontally only when it can't.
          <div className="w-full overflow-x-auto scrollbar-hide pb-4">
            <div className="flex items-end gap-2 sm:gap-3 px-4 mx-auto" style={{ width: 'max-content' }}>
              {leaders.map((l, i) => {
                const dist = Math.abs(i - fanCenter);
                // Graduated widths make the fan arc (bigger toward the centre).
                const width = 176 - dist * 16;
                return (
                  <div key={l.key} className="flex-shrink-0" style={{ zIndex: 20 - dist }}>
                    <PlayerCard
                      player={l.player}
                      badge={l.badge}
                      accent={l.accent}
                      imageUrl={l.image}
                      highlight={{ value: l.player[l.key] ?? 0, label: l.label, key: l.key }}
                      width={width}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 text-center mt-4">
          <Link to="/leaderboards" className="inline-flex items-center gap-1 text-sm font-semibold text-gray-400 hover:text-white uppercase tracking-wide">
            {t('home.seeFullBoard', { defaultValue: 'See the full board' })} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ===================== TOP CLUBS (podium) ===================== */}
      <section className="border-y border-white/[0.06] bg-white/[0.02]">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <DisplayTitle pre={t('home.top', { defaultValue: 'TOP' })} accent={t('home.clubs', { defaultValue: 'CLUBS' })} className="text-3xl sm:text-4xl" />
            <p className="text-gray-400 mt-2">{t('home.clubsSub', { defaultValue: 'The most decorated clubs by titles won.' })}</p>
          </div>
          {topClubs.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">{t('home.noTeams', { defaultValue: 'No teams yet.' })}</p>
          ) : (
            <div className="flex items-end justify-center gap-8 sm:gap-16">
              {podiumOrder.map((club) => {
                const rank = topClubs.indexOf(club) + 1;
                const m = PODIUM[rank] || PODIUM[3];
                const wins = club.match_wins ?? club.total_wins ?? 0;
                const wr = club.total_matches ? Math.round((wins / club.total_matches) * 100) : (club.win_rate ?? 0);
                const ownerId = ownerIds[club.team_id];
                const ownerName = club.owner_name || club.owner || 'Owner';
                return (
                  <div key={club.team_id} className="flex flex-col items-center text-center">
                    <Link
                      to={club.team_id ? `/teams/${club.team_id}` : '/teams'}
                      className="flex flex-col items-center text-center group"
                    >
                      <span
                        className="mb-3 px-2.5 py-0.5 rounded-md text-[11px] font-extrabold uppercase tracking-wide"
                        style={{ backgroundColor: m.accent, color: '#0a1410' }}
                      >
                        {m.place}
                      </span>
                      <ClubCrest logo={club.team_logo} name={club.team_name} accent={m.accent} big={m.big} />
                      <div className={`font-bebas text-white leading-none tracking-wide ${m.big ? 'text-3xl' : 'text-2xl'}`}>
                        {(club.team_name || 'Club').toUpperCase()}
                      </div>
                      <div className="flex items-baseline gap-1.5 mt-1.5">
                        <span className={`font-bebas leading-none ${m.big ? 'text-4xl' : 'text-3xl'}`} style={{ color: m.accent }}>
                          {club.tournaments_won ?? 0}
                        </span>
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          {t('home.titlesShort', { defaultValue: 'Titles' })}
                        </span>
                      </div>
                      <div className="text-sm text-gray-300 mt-1.5 font-mono">{wr}% WR · {wins} W</div>
                    </Link>
                    {ownerId ? (
                      <Link to={`/profile/${ownerId}`} className="text-[11px] text-gray-500 hover:text-white transition-colors mt-1">
                        by {ownerName}
                      </Link>
                    ) : (
                      <div className="text-[11px] text-gray-500 mt-1">by {ownerName}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-center mt-8">
            <Link to="/leaderboards?tab=teams" className="inline-flex items-center gap-1 text-sm font-semibold text-gray-400 hover:text-white uppercase tracking-wide">
              {t('common.viewAll', { defaultValue: 'View all clubs' })} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FEATURE BANNER ===================== */}
      <section className="relative overflow-hidden border-y border-white/[0.06]">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, #12151e 40%, rgba(49,197,144,0.10) 75%, rgba(35,248,181,0.14) 100%)' }} />
        {/* Dot grid pattern */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            maskImage: 'radial-gradient(120% 100% at 30% 50%, #000 35%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(120% 100% at 30% 50%, #000 35%, transparent 80%)'
          }}
        />
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(49,197,144,0.15)' }} />
        <div className="relative max-w-6xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TEAL }}>
              {t('home.practice', { defaultValue: 'Practice' })}
            </span>
            <DisplayTitle
              pre={t('home.scrimPre', { defaultValue: 'FIND YOUR NEXT' })}
              accent={t('home.scrimAccent', { defaultValue: 'SCRIM' })}
              className="text-4xl sm:text-5xl mt-3 mb-4"
            />
            <p className="text-gray-400 max-w-md mb-6">
              {t('home.scrimDesc', {
                defaultValue: 'Match teams your level by region and platform, with automatic scheduling and result tracking.'
              })}
            </p>
            <Link
              to="/lfs/schedule"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-zinc-900 transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: TEAL }}
            >
              <Swords className="w-5 h-5" />
              {t('home.findScrim', { defaultValue: 'Find a scrim' })}
            </Link>
          </div>
          <div className="hidden md:flex justify-center">
            <div className="relative">
              <div className="w-56 h-56 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm flex items-center justify-center">
                <Swords className="w-28 h-28" style={{ color: TEAL }} />
              </div>
              <div className="absolute -inset-4 rounded-3xl blur-2xl -z-10" style={{ background: 'rgba(49,197,144,0.2)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TOURNAMENTS SHOWCASE ===================== */}
      <section className="relative overflow-hidden py-16">
        {/* Video background */}
        <video
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          poster="/background3.webp"
        >
          <source src="/background/tournaments.webm" type="video/webm" />
        </video>
        {/* Same glassy foggy filter as the hero */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(70% 90% at 50% 0%, rgba(49,197,144,0.16), rgba(18, 21, 30,0) 65%)' }} />
        <div className="absolute inset-0 backdrop-blur-[5px] bg-white/[0.03]" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(100% 65% at 50% 40%, rgba(255,255,255,0.06), rgba(255,255,255,0) 55%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(178,220,208,0.10), rgba(150,206,190,0) 40%, rgba(150,206,190,0.14))' }} />
        {/* Dark gradient — darker at the edges so it blends into the page */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18, 21, 30,0.97), rgba(18, 21, 30,0.62) 28%, rgba(18, 21, 30,0.62) 72%, rgba(18, 21, 30,0.97))' }} />

        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <DisplayTitle
              pre={t('home.liveAnd', { defaultValue: 'LIVE & UPCOMING' })}
              accent={t('home.tournamentsWord', { defaultValue: 'TOURNAMENTS' })}
              className="text-3xl sm:text-4xl"
            />
            <p className="text-gray-400 mt-2">{t('home.tournamentsSub', { defaultValue: 'Jump into the action or register your team.' })}</p>
          </div>

          {showcase.length === 0 ? (
            <div className="rounded-2xl border border-white/[0.08] bg-zinc-900/40 backdrop-blur-sm p-12 text-center">
              <Trophy className="w-10 h-10 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400">{t('home.noTournaments', { defaultValue: 'No tournaments scheduled right now.' })}</p>
              <Link to="/tournaments" className="inline-block mt-4 px-5 py-2 rounded-xl font-semibold text-zinc-900" style={{ backgroundColor: TEAL }}>
                {t('home.browseTournaments', { defaultValue: 'Browse tournaments' })}
              </Link>
            </div>
          ) : (
            <TournamentCarousel items={showcase} renderCard={(tour) => renderTournamentCard(tour, t)} />
          )}

          <div className="text-center mt-8">
            <Link to="/tournaments" className="inline-flex items-center gap-1 text-sm font-semibold text-gray-300 hover:text-white uppercase tracking-wide">
              {t('home.seeAllTournaments', { defaultValue: 'See all tournaments' })} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== COMMUNITY / CALENDAR CTA ===================== */}
      <section className="max-w-6xl mx-auto px-4 pt-16 pb-24 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Calendar */}
        <Link
          to="/calendar"
          className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 hover:border-[#31C590]/40 transition-all duration-300 group"
        >
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex-shrink-0">
            <Calendar className="w-6 h-6" style={{ color: TEAL }} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white">{t('home.calendarTitle', { defaultValue: 'Your match calendar' })}</h3>
            <p className="text-sm text-gray-400">{t('home.calendarSubtitle', { defaultValue: 'Upcoming and unscheduled matches for your teams.' })}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
        </Link>

        {/* Discord */}
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/[0.06] p-6 hover:border-indigo-400/40 transition-all duration-300 group"
        >
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-500/15 border border-indigo-400/20 flex-shrink-0">
            <Target className="w-6 h-6 text-indigo-300" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white">{t('home.communityTitle', { defaultValue: 'Join the community' })}</h3>
            <p className="text-sm text-gray-400">{t('home.communitySubtitle', { defaultValue: 'Find teammates, scrims and tournaments on Discord.' })}</p>
          </div>
          <span className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold transition-colors flex-shrink-0">
            {t('home.joinDiscord', { defaultValue: 'Join' })}
          </span>
        </a>
      </section>
    </div>
  );
};

export default Home;
